import {
  ApiBill,
  BillCategory,
  BillStatus,
  BillType,
  CurrencyCode,
} from "../../../types/Bill.types";
import { getFormattedDate } from "../../utils/getFormattedDate";
import { MockPolicyId, mockPolicies } from "../mockPolicies";

const dummyBillingUrl = "https://example.com/billing";

export const hdcInfo = [
  { ordinal: "1st", cost: 45 },
  { ordinal: "2nd", cost: 70 },
  { ordinal: "3rd", cost: 175 },
  { ordinal: "VIP", cost: 0 },
];

const autoPayFailedFields: Pick<
  ApiBill,
  "type" | "autoPayMethodDetails" | "informAboutStatus" | "status"
> = {
  type: BillType.AutoPay,
  autoPayMethodDetails: {
    last4: "1234",
  },
  informAboutStatus: true,
  status: BillStatus.Failed,
};

const autoPayUpcomingFields: Pick<
  ApiBill,
  "type" | "autoPayMethodDetails" | "informAboutStatus" | "status"
> = {
  type: BillType.AutoPay,
  autoPayMethodDetails: {
    last4: "1234",
  },
  informAboutStatus: true,
  status: BillStatus.Ok,
};

const revalOfferFields: Pick<ApiBill, "revalOffer"> = {
  revalOffer: {
    // Check when test accounts will exist if currentPremium is formatted or not
    currentPremium: "5000",
    endoNumber: "123123123",
    endoStatus: "UNKN",
    newPremium: "6000",
    policyNumber: "id123",
    premiumDifference: "1000",
    vehicleValues: [],
  },
};

const pastDueFields: Pick<ApiBill, "type" | "informAboutStatus" | "status"> = {
  type: BillType.PayOnce,
  informAboutStatus: true,
  status: BillStatus.PastDue,
};

const upcomingFields: Pick<ApiBill, "type" | "informAboutStatus" | "status"> = {
  type: BillType.PayOnce,
  informAboutStatus: true,
  status: BillStatus.Ok,
};

type BillDetails = {
  id: ApiBill["id"];
  type?: ApiBill["type"];
  status?: ApiBill["status"];
  informAboutStatus?: ApiBill["informAboutStatus"];
  amount: ApiBill["amount"];
  formattedAmount: ApiBill["formattedAmount"];
  category: ApiBill["category"];
  productNumber: ApiBill["productNumber"];
  autoPayMethodDetails?: ApiBill["autoPayMethodDetails"];
  productDetails: ApiBill["productDetails"];
};

export const createBill = ({
  id,
  productNumber,
  category,
  type,
  status,
  informAboutStatus,
  autoPayMethodDetails = undefined,
  amount,
  formattedAmount,
  productDetails,
}: BillDetails): ApiBill => ({
  productNumber,
  formattedProductNumber: productNumber,
  category,
  id: id ? id : "1",
  type: type ? type : BillType.PayOnce,
  amount: amount ? amount : 500000,
  formattedAmount: formattedAmount ? formattedAmount : "$5,000",
  autoPayMethodDetails: autoPayMethodDetails,
  currency: CurrencyCode.Usd,
  nextPaymentDate:
    status === (BillStatus.Ok || undefined)
      ? getFormattedDate({ days: 7 })
      : getFormattedDate({ days: -7 }),
  expirationDate:
    status === (BillStatus.Ok || undefined)
      ? getFormattedDate({ days: 7 })
      : getFormattedDate({ days: -7 }),
  paymentUrl: dummyBillingUrl,
  informAboutStatus: informAboutStatus ? informAboutStatus : false,
  status: status ? status : BillStatus.Ok,
  productDetails,
  total: {
    amount: amount ? amount : 500000,
    formattedAmount: formattedAmount ? formattedAmount : "$5,000",
  },
  revalOffer: null,
});

type InsuranceBillDetails = Partial<BillDetails> & {
  id: string;
  policyNumber: MockPolicyId;
};

export const createInsuranceBill = ({
  id,
  policyNumber,
  ...rest
}: InsuranceBillDetails): ApiBill => {
  const policy = mockPolicies[policyNumber];
  const { vehicles } = policy;

  return createBill({
    category: BillCategory.Insurance,
    ...rest,
    id,
    amount: rest.amount ? rest.amount : 12300,
    formattedAmount: rest.formattedAmount ? rest.formattedAmount : "$123",
    productNumber: rest.productNumber
      ? rest.productNumber
      : policyNumber.toString(),
    // Populate productDetails with the actual vehicles from the policy
    productDetails: rest.productDetails
      ? rest.productDetails
      : vehicles.map(({ year, make, model }) => `${year} ${make} ${model}`),
  });
};

export const insuranceAutoPayFailed = (
  billDetails: InsuranceBillDetails
): ApiBill => createInsuranceBill({ ...billDetails, ...autoPayFailedFields });

export const insuranceAutoPayUpcoming = (
  billDetails: InsuranceBillDetails
): ApiBill => createInsuranceBill({ ...billDetails, ...autoPayUpcomingFields });

export const insuranceBillPastDue = (
  billDetails: InsuranceBillDetails
): ApiBill => createInsuranceBill({ ...billDetails, ...pastDueFields });

export const insuranceBillUpcoming = (
  billDetails: InsuranceBillDetails
): ApiBill => createInsuranceBill({ ...billDetails, ...upcomingFields });

export const insuranceBillWithRevalOffer = (
  billDetails: InsuranceBillDetails
): ApiBill => ({
  ...createInsuranceBill({ ...billDetails }),
  ...revalOfferFields,
});

type HdcBillDetails = Partial<BillDetails> & {
  id: string;
  billType?: "1.0" | "1.5";
  level?: 1 | 2 | 3;
};

export const createHdcBill = ({
  id = "1",
  billType = "1.5",
  level = 2,
  ...rest
}: HdcBillDetails): ApiBill =>
  createBill({
    id,
    productNumber: "1",
    category:
      billType === "1.5" ? BillCategory.Hdc1Dot5 : BillCategory.Hdc1Dot0,
    amount: hdcInfo[level - 1].cost * 100,
    formattedAmount: hdcInfo[level - 1].cost.toString(),
    productDetails: [`${hdcInfo[level - 1].ordinal} gear`],
    ...rest,
  });

export const hdcAutoPayAlertMock = (
  billDetails: HdcBillDetails = { id: "1" }
): ApiBill => createHdcBill({ ...billDetails, ...autoPayFailedFields });

export const hdcAutoPayUpcomingMock = (billDetails: HdcBillDetails): ApiBill =>
  createHdcBill({ ...billDetails, ...autoPayUpcomingFields });

export const hdcBillPastDueMock = (billDetails: HdcBillDetails): ApiBill =>
  createHdcBill({ ...billDetails, ...pastDueFields });

export const hdcBillUpcomingMock = (billDetails: HdcBillDetails): ApiBill =>
  createHdcBill({ ...billDetails, ...upcomingFields });
